import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Typography } from "antd";
import { Modal } from "@libertify/ui";
import { SlideScriptEditor } from "./SlideScriptEditor";

// Single-slide script editing surface used both for the currently selected
// slide and for each slide's block inside the whole-script view. Owns the
// "text became empty" flow: instead of persisting an empty narration, it asks
// the user to confirm the slide should be removed entirely.
export const SlideScriptPanel = ({
  initialScript,
  isRTL: rtl,
  language,
  onSave,
  onDelete,
  textOnly = false,
  sectionId,
  voiceId,
}: {
  initialScript: string;
  isRTL: boolean;
  language?: string;
  onSave: (content: string) => void;
  onDelete: () => void;
  textOnly?: boolean;
  sectionId?: string;
  voiceId?: string;
}) => {
  const t = useTranslate();
  const [reloadKey, setReloadKey] = useState(0);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handleSave = (edited: string) => {
    if (edited.trim() === "") {
      setConfirmingDelete(true);
      return;
    }
    onSave(edited);
  };

  return (
    <>
      <SlideScriptEditor
        key={reloadKey}
        script={initialScript}
        index={0}
        isRTL={rtl}
        language={language}
        onSave={handleSave}
        textOnly={textOnly}
        sectionId={sectionId}
        voiceId={voiceId}
      />
      <Modal
        open={confirmingDelete}
        title={t(
          "components.templates.settings.scriptTab.deleteEmptySlideTitle"
        )}
        danger
        confirmText={t("buttons.delete")}
        cancelText={t("buttons.cancel")}
        onConfirm={() => {
          setConfirmingDelete(false);
          onDelete();
        }}
        onClose={() => {
          setConfirmingDelete(false);
          // Re-feed the held narration to a fresh editor instance.
          setReloadKey((key) => key + 1);
        }}
        styles={{
          content: {
            padding: 0,
          },
        }}
      >
        <Typography.Text>
          {t("components.templates.settings.scriptTab.deleteEmptySlideContent")}
        </Typography.Text>
      </Modal>
    </>
  );
};
